// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { put, select, takeLatest, takeEvery } from "redux-saga/effects";
import { Store } from "@reduxjs/toolkit";

import {
  ActionType,
  raiseAction,
  IAction,
  IActionChangeInput,
  IActionChangeOutput,
  IActionAckAlarm,
  IActionTypeLogin,
} from "actions";
import { IState } from "states/states";

import { AlarmState, AlarmSource } from "comtypes";
import { ComInterface } from "cominterface";
import { DataType, extractArrayData } from "apiutils";

export interface IDataLivePower {
  curTimeOfPeriod: string;
  curEnergy: string;
  curPower: string;
  curTargetValues: string;
  curRegulationPriorityStatus: string;
  curTrendLevel: string;
  energyMeterValues: string;
  inputStatus: string;
  outputStatus: string;
  outputStatusPhysical: string;
  conditionStatus: string;
  moduleStatus?: string; // provided since engine v7.6.306
}
export interface IDataLiveCharger {
  chargerCurP: string;
  chargerCurI: string;
  chargerCurU: string;
  chargerDevMaxI: string;
  chargerSetMaxI: string;
  chargerETotal: string;
  chargerESession: string;
  chargerState: string;
  chargerAuth: string;
}
export interface IDataLiveChargerPing {
  pingResult: string;
}
export type IDataAll = {
  siteId?: number;
  systemTime: number;
} & Partial<IDataLivePower> &
  Partial<IDataLiveCharger> &
  Partial<IDataLiveChargerPing>;

// number of trend items to load in a single ajax request
export const NUM_OPC_TREND_ITEMS = 500; // 500 seems to be the limitation on LINX side

// keep consistent with newAlarmData.ts:makeXaid()
export function makeXaid(source: AlarmSource, value: number) {
  const sourceStr = source.substr(source.indexOf("_") + 1);
  return `${sourceStr}${value}`.substr(0, 24); // max 24 char of DB field
}

export function getAlarmState(isActive: boolean, isAcked: boolean) {
  if (isActive && !isAcked) return AlarmState.ACTIVE;
  if (!isActive && !isAcked) return AlarmState.ACK_PENDING;
  if (isActive && isAcked) return AlarmState.ACKD_ACTIVE;
  if (!isActive && isAcked) return AlarmState.ACKD;
  console.error("illegal alarm state");
  return AlarmState.ACTIVE;
}

export abstract class ApiSagas {
  protected comInterface: ComInterface | undefined;

  // main entry point to load sagas at startup
  public *initSagas() {
    yield takeLatest(ActionType[ActionType.ACT_LOGIN_REQ], this.doLogin.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_LOGOUT_REQ], this.doLogout.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_PARAMETER_REQ], this.getParameters.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_ALARMS_REQ], this.getAlarms.bind(this));
    yield takeEvery(ActionType[ActionType.ACT_ACK_ALARM_REQ], this.ackAlarm.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_TRENDINFO_REQ], this.getTrendInfo.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_TREND_REQ], this.getTrend.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_IOTREND_REQ], this.getIoTrend.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_CHANGE_INPUT_REQ], this.setInput.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_CHANGE_OUTPUT_REQ], this.setOutput.bind(this));
  }

  public abstract initApi(store?: Store): any;
  public abstract doAutoLogin(): any;

  protected abstract doLogin(action: IAction<IActionTypeLogin>): any;
  protected abstract doLogout(action: IAction<undefined>): any;
  protected abstract getParameters(action: IAction<undefined>): any;
  protected abstract getAlarms(action: IAction<undefined>): any;
  protected abstract getTrendInfo(action: IAction<undefined>): any;
  protected abstract getTrend(action: IAction<undefined>): any;
  protected abstract getIoTrend(action: IAction<undefined>): any;
  protected abstract setInput(action: IAction<IActionChangeInput>): any;
  protected abstract setOutput(action: IAction<IActionChangeOutput>): any;
  protected abstract ackAlarm(action: IAction<IActionAckAlarm>): any;

  protected getState = (state: IState) => state;

  protected *raiseHttpError(
    actionFail: ActionType,
    error: unknown /* typically Response | Error */,
  ) {
    // yield error action; convert error object to string to have it readable in Redux action viewer
    let errString: string | undefined;

    if ((error as any).hasOwnProperty("errors")) {
      try {
        errString = ((error as any).errors as Error[])
          .map((curErr) => {
            return curErr.message;
          })
          .join(", ");
      } catch {
        // nothing to do here, try again with other content below
      }
    }
    if (errString === undefined) {
      // we assume this is an error from the http request?
      if (error instanceof Response && error.statusText) {
        // if we cannot get further details, we provide the http error as the only error
        errString = error.statusText;
      } else if (error instanceof Error && error.message) {
        errString = error.message;
      }
    }
    // nothing found
    if (errString === undefined) {
      errString = "Unknown error.";
    }
    yield put(raiseAction(actionFail, errString));

    // is this an http unauthorized error?
    // only report ACT_HTTP_ERR_UNAUTHORIZED if this was no login attempt
    const authorizeRequests = [ActionType.ACT_LOGIN_FAIL];
    if (
      error instanceof Response &&
      error.status === 401 &&
      !authorizeRequests.includes(actionFail)
    ) {
      // this typically happens on session expiration
      yield put(raiseAction(ActionType.ACT_HTTP_ERR_UNAUTHORIZED));
      // let main scheduler task start immediately
      yield put(raiseAction(ActionType.ACT_AWAKE));
    }
  }

  protected *checkForNewPeriod(curTimeOfPeriod: string) {
    const state = (yield select(this.getState)) as IState;
    const timeOfPeriod = extractArrayData(
      curTimeOfPeriod,
      DataType.INT,
      state.parameter?.tariffline.length,
    );
    let hasPeriodSwitch = false;
    state.data.tariffcircuit.forEach((curTariff, curIndex) => {
      if (curTariff.timeOfPeriod > timeOfPeriod[curIndex]) {
        hasPeriodSwitch = true;
      }
    });
    if (hasPeriodSwitch) {
      yield put(raiseAction(ActionType.ACT_NEW_PERIOD));
    }
  }
}
